import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import type { Attachment } from '../types';

export interface AttachmentPayload {
  name: string | null;
  data_url: string;
  url: string | null;
}

/**
 * Puts a composer attachment somewhere the agent can point at.
 *
 * The model is shown the picture as a data URL either way; what the upload
 * buys is a real site path, without which the agent can describe an image
 * but not place it. A failed upload therefore degrades rather than fails:
 * `url` is null and the attachment travels view-only.
 *
 * Subclass `contentPayload` to file uploads somewhere else than as an Image
 * inside the page — a media folder, a DAM, a customer's own content type.
 */
export class AttachmentUploader {
  protected readonly dataUrlPattern = /^data:([^;,]+);base64,(.*)$/;

  /** The site session, read from the cookie Volto stores it in. */
  protected authHeaders(): Record<string, string> {
    const token =
      typeof document !== 'undefined' &&
      document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith('auth_token='));

    return token
      ? { Authorization: `Bearer ${token.slice('auth_token='.length)}` }
      : {};
  }

  protected contentPayload(
    filename: string,
    mime: string,
    data: string,
  ): Record<string, unknown> {
    return {
      '@type': 'Image',
      title: filename,
      image: { data, encoding: 'base64', 'content-type': mime, filename },
    };
  }

  /** Where the content is created. The page itself, so it travels with it. */
  protected target(pathname: string): string {
    return `/++api++${pathname === '/' ? '' : pathname}`;
  }

  async upload(
    attachment: Attachment,
    pathname: string,
  ): Promise<string | null> {
    const match = this.dataUrlPattern.exec(attachment.dataUrl || '');
    if (!match) return null;
    const [, mime, data] = match;
    if (!mime.startsWith('image/')) return null;
    const filename = attachment.name || 'attachment';

    try {
      const response = await fetch(this.target(pathname), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...this.authHeaders(),
        },
        body: JSON.stringify(this.contentPayload(filename, mime, data)),
      });
      if (!response.ok) return null;
      const content = await response.json();

      return content['@id'] ? flattenToAppURL(content['@id']) : null;
    } catch {
      return null;
    }
  }

  async uploadAll(
    attachments: Attachment[],
    pathname: string,
  ): Promise<AttachmentPayload[]> {
    const payloads: AttachmentPayload[] = [];
    for (const attachment of attachments) {
      payloads.push({
        name: attachment.name || null,
        data_url: attachment.dataUrl,
        url: await this.upload(attachment, pathname),
      });
    }

    return payloads;
  }
}
